// react
import { useContext, useState } from 'react';

// types
import { Account, ActionButton, Pagination } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// cards
import { CardAccount } from '../../../cards/CardAccount/CardAccount';
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// props
export interface TableAccountsProps {
	accounts: Account[];
	hasFilters?: boolean;
	hasSearch?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchAccounts?: () => void;
	type?: Account['type'];
}

export const TableAccounts = ({
	accounts,
	hasFilters = true,
	hasSearch = true,
	isLoading,
	pagination,
	refetchAccounts,
	type = 'DESIGNATED',
}: TableAccountsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramStatus = searchParams.get('status');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// defaults
	const defaultFiltersValues = {
		status: ['ACTIVE'],
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		status: paramStatus ? paramStatus.split(',') : defaultFiltersValues.status,
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// vars
	const emptyActions: ActionButton[] = [];
	const idFormFilters = 'form-accounts-filters';

	if (!isLoading && userProjectPermissions?.account?.create) {
		emptyActions.push({
			children: t('addAccount'),
			onClick: () => {
				searchParams.set('dialog', 'addAccount');
				searchParams.set('type', type);
				setSearchParams(searchParams);
			},
			variant: 'contained' as ActionButton['variant'],
		});
	}

	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			hasSearchParam: true,
			id: `${idFormFilters}-status`,
			label: t('status'),
			multiple: true,
			name: 'status',
			onChange: handleChangeFilterValue,
			options: [
				{
					label: t('active'),
					value: 'ACTIVE',
				},
				{
					label: t('deactivated'),
					value: 'DEACTIVATED',
				},
			],
			type: 'SELECT',
			value: filtersValues.status,
		},
	];

	return (
		<Grid container spacing={2}>
			{(hasFilters || hasSearch) && (
				<Grid item xs={12}>
					<Grid container justifyContent="flex-end" spacing={2}>
						{hasSearch && (
							<Grid item xs={12} md={true}>
								<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
							</Grid>
						)}
						{hasFilters && (
							<Grid item xs={12} md="auto">
								<BtnFilters
									filters={filters}
									onReset={() => {
										setFiltersValues(defaultFiltersValues);
									}}
								/>
							</Grid>
						)}
					</Grid>
				</Grid>
			)}
			{!isLoading && accounts.length > 0 ? (
				accounts.map((account, i) => {
					return (
						<Grid item key={`account-${type}-${i}`} xs={12}>
							<CardAccount account={account} refetchAccounts={refetchAccounts} />
						</Grid>
					);
				})
			) : (
				<Grid item xs={12}>
					<CardEmpty
						empty={{
							actions: emptyActions,
							content: {
								children:
									type === 'DESIGNATED'
										? t('empty.accounts.designated.content')
										: t('empty.accounts.local.content'),
							},

							title: { children: t('empty.accounts.title') },
						}}
						isLoading={isLoading}
					/>
				</Grid>
			)}
			<Grid item xs={12}>
				<TableFooter numShowing={accounts.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
